import { WORKFLOW_EDGE_OPTIONS } from "./workflowBuilderTheme";
import {
  normalizeNodeConfigForPalette,
  sanitizeNodeConfigForSave,
  getFlatVariables,
  getClearKeys,
} from "./workflowInspectorUx";
import {
  deserializeConditionsFromApi,
  serializeConditionsForSave,
} from "./workflowSchemaUtils";
import {
  ACTION_FAILURE_HANDLE,
  ACTION_SUCCESS_HANDLE,
  COLLECT_RECEIVED_HANDLE,
  CONDITION_FALSE_HANDLE,
  CONDITION_TRUE_HANDLE,
  FALLBACK_HANDLE,
  INVOKE_COMPLETED_HANDLE,
  LINEAR_HANDLE,
  TIMEOUT_HANDLE,
  inferPaletteTypeFromLegacyNode,
  inferWorkflowTypeFromNode,
  isLinearMessagePalette,
  isMapTransitionPalette,
  isPaletteDrivenNode,
  paletteToCanvasType,
  getNodeDescription,
  supportsTimeoutRef,
  usesOnSuccessRef,
  usesTransitions,
  PALETTE,
} from "./workflowPaletteConstants";

export const SESSION_TIMEOUT_MIN_SECONDS = 60;
export const SESSION_TIMEOUT_MAX_SECONDS = 3600;
export const DEFAULT_SESSION_TIMEOUT_SECONDS = 300;

const getOutgoingByHandle = (nodeId, edges) =>
  edges
    .filter((edge) => edge.source === nodeId && edge.target)
    .reduce((acc, edge) => {
      const handle = edge.sourceHandle || LINEAR_HANDLE;
      acc[handle] = edge.target;
      return acc;
    }, {});

/** True for canvas nodes or API nodes that terminate the workflow. */
export function isWorkflowEndNode(node) {
  if (!node) return false;
  if (node.type === "END") return true;

  const data = node.data || {};
  return data.workflowType === "END" || data.paletteType === PALETTE.END;
}

/** Entry node = no incoming edges, excluding END (which is never a valid start). */
export function inferWorkflowStartId(nodes = [], edges = []) {
  if (!nodes.length) return "";

  const incomingTargets = new Set(edges.map((edge) => edge.target).filter(Boolean));
  const candidates = nodes.filter(
    (node) => !incomingTargets.has(node.id) && !isWorkflowEndNode(node)
  );

  if (candidates.length === 1) return candidates[0].id;

  if (candidates.length > 1) {
    const sorted = [...candidates].sort((a, b) => {
      const dy = (a.position?.y ?? 0) - (b.position?.y ?? 0);
      if (dy !== 0) return dy;
      return (a.position?.x ?? 0) - (b.position?.x ?? 0);
    });
    return sorted[0].id;
  }

  const firstNonEnd = nodes.find((node) => !isWorkflowEndNode(node));
  return firstNonEnd?.id || nodes[0]?.id || "";
}

function resolveWorkflowStart(nodes = [], edges = [], preferredStart = "") {
  const inferred = inferWorkflowStartId(nodes, edges);
  if (!preferredStart) return inferred;

  const preferred = nodes.find((node) => node.id === preferredStart);
  if (!preferred || isWorkflowEndNode(preferred)) return inferred;

  const hasIncoming = edges.some((edge) => edge.target === preferredStart);
  if (hasIncoming && inferred) return inferred;

  return preferredStart;
}

const makeEdge = (source, target, sourceHandle) => ({
  id: `e_${source}_${target}_${sourceHandle}`,
  source,
  target,
  sourceHandle,
  ...WORKFLOW_EDGE_OPTIONS
});

const stripEmpty = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, value]) => value != null && value !== ""));

const readApiNodePosition = (apiNode) => {
  const source = apiNode?.position ?? apiNode?.coordinates;
  if (!source || typeof source !== "object") return null;

  const x = source.x;
  const y = source.y;
  if (Number.isFinite(x) && Number.isFinite(y)) {
    return { x, y };
  }

  return null;
};

const defaultGridPosition = (index) => ({
  x: 250 + (index % 3) * 280,
  y: 120 + Math.floor(index / 3) * 220
});

const resolveCanvasPosition = (apiNode, index) =>
  readApiNodePosition(apiNode) ?? defaultGridPosition(index);

const attachCanvasPosition = (apiNode, canvasNode) => {
  const position = readApiNodePosition(canvasNode);
  return position ? { ...apiNode, position } : apiNode;
};

const NODE_ROOT_FIELDS = ["saveToContext", "logic", "conditions"];

const LEGACY_HANDLE_MAP = {
  a: LINEAR_HANDLE,
  yes: CONDITION_TRUE_HANDLE,
  no: CONDITION_FALSE_HANDLE
};

const transitionOutgoing = (outgoing) =>
  Object.fromEntries(Object.entries(outgoing).filter(([key]) => key !== TIMEOUT_HANDLE));

const resolveTransitionHandleFromApi = (key, paletteType, config = {}) => {
  if (key === FALLBACK_HANDLE && isLinearMessagePalette(paletteType, config)) {
    return LINEAR_HANDLE;
  }
  return LEGACY_HANDLE_MAP[key] || key;
};

const serializeTransitionMap = (paletteType, outgoing, config = {}) => {
  const routed = transitionOutgoing(outgoing);

  if (isMapTransitionPalette(paletteType, config)) {
    return stripEmpty(routed);
  }

  const transitions = {};
  if (routed[LINEAR_HANDLE]) {
    transitions[FALLBACK_HANDLE] = routed[LINEAR_HANDLE];
  }
  Object.entries(routed).forEach(([key, target]) => {
    if (key !== LINEAR_HANDLE) {
      transitions[key] = target;
    }
  });
  return stripEmpty(transitions);
};

/** GET_WORKFLOW node root fields are edited on canvas data, not inside config. */
const normalizeApiNodeConfig = (apiNode) => {
  const config = { ...(apiNode.config || {}) };
  NODE_ROOT_FIELDS.forEach((field) => delete config[field]);

  if (inferWorkflowTypeFromNode(apiNode) === "ACTION") {
    if (apiNode.action) config.action = apiNode.action;
    
    const actionName = apiNode.action || config.action;
    let input = apiNode.input || {};

    const actionHandlers = {
      SET_VARIABLE: () => {
        const targetKey = ["variables", "params"].find((k) => k in input);
        const rawVars = targetKey ? input[targetKey] : input;
        
        if (Array.isArray(rawVars)) {
          config.input = { variables: rawVars };
        } else {
          config.input = { variables: Object.entries(rawVars || {}).map(([key, value]) => ({ key, value })) };
        }
      },
      CLEAR_VARIABLE: () => {
        const resolver = [
          { test: () => Array.isArray(input.keys), exec: () => input.keys },
          { test: () => input.variables, exec: () => Array.isArray(input.variables) ? input.variables.map(v => v?.key || v) : Object.keys(input.variables) },
          { test: () => input.params, exec: () => Array.isArray(input.params) ? input.params.map(v => v?.key || v) : Object.keys(input.params) },
          { test: () => input.keys && typeof input.keys === "object", exec: () => Object.keys(input.keys) }
        ].find((r) => r.test());
        
        const keys = resolver ? resolver.exec() : Object.keys(input).filter(k => k !== "keys" && k !== "variables" && k !== "params");
        config.input = {
          variables: keys.map(key => ({ key, value: "" }))
        };
      }
    };

    if (actionHandlers[actionName]) {
      actionHandlers[actionName]();
    } else {
      if (apiNode.input) config.input = apiNode.input;
    }
    
    if (apiNode.retryPolicy) config.retryPolicy = apiNode.retryPolicy;
  }

  return config;
};

const addTransitionsFromMap = (apiNode, paletteType, addEdge, config = {}) => {
  if (!apiNode.transitions || Array.isArray(apiNode.transitions)) return false;

  Object.entries(apiNode.transitions).forEach(([key, target]) => {
    if (key === TIMEOUT_HANDLE) return;
    addEdge(resolveTransitionHandleFromApi(key, paletteType, config), target);
  });
  return true;
};

const nodeToFlowFragments = (apiNode) => {
  const paletteType = inferPaletteTypeFromLegacyNode(apiNode);
  const workflowType = inferWorkflowTypeFromNode(apiNode);

  const canvasNode = {
    id: apiNode.id,
    type: paletteToCanvasType(paletteType),
    position: { x: 0, y: 0 },
    data: {
      label: apiNode.name || apiNode.id,
      description: getNodeDescription(apiNode),
      hasError: false,
      paletteType,
      workflowType,
      config: normalizeNodeConfigForPalette(
        normalizeApiNodeConfig(apiNode),
        paletteType,
        apiNode.name || apiNode.id
      ),
      logic: apiNode.logic ?? apiNode.config?.logic,
      conditions: deserializeConditionsFromApi(
        apiNode.conditions ?? apiNode.config?.conditions
      ),
      saveToContext: apiNode.saveToContext ?? apiNode.config?.saveToContext
    }
  };

  const edges = [];
  const addEdge = (sourceHandle, target) => {
    if (target) edges.push(makeEdge(apiNode.id, target, sourceHandle));
  };

  const attachTimeoutEdge = () => {
    if (supportsTimeoutRef(workflowType) && apiNode.onTimeout) {
      addEdge(TIMEOUT_HANDLE, apiNode.onTimeout);
    }
  };

  const finish = () => {
    attachTimeoutEdge();
    return { node: canvasNode, edges };
  };

  if (usesOnSuccessRef(workflowType)) {
    addEdge(ACTION_SUCCESS_HANDLE, apiNode.onSuccess);
    return { node: canvasNode, edges };
  }

  if (workflowType === "CONDITION") {
    addEdge(CONDITION_TRUE_HANDLE, apiNode.onTrue || apiNode.transitions?.onTrue || apiNode.transitions?.yes);
    addEdge(CONDITION_FALSE_HANDLE, apiNode.onFalse || apiNode.transitions?.onFalse || apiNode.transitions?.no);
    return { node: canvasNode, edges };
  }

  if (workflowType === "COLLECT_INPUT") {
    addEdge(COLLECT_RECEIVED_HANDLE, apiNode.onReceived);
    return finish();
  }

  if (workflowType === "INVOKE_WORKFLOW") {
    addEdge(INVOKE_COMPLETED_HANDLE, apiNode.onCompleted);
    return { node: canvasNode, edges };
  }

  if (workflowType === "ACTION") {
    addEdge(ACTION_SUCCESS_HANDLE, apiNode.onSuccess);
    addEdge(ACTION_FAILURE_HANDLE, apiNode.onFailure);
    return { node: canvasNode, edges };
  }

  if (addTransitionsFromMap(apiNode, paletteType, addEdge, canvasNode.data.config)) {
    return finish();
  }

  if (Array.isArray(apiNode.transitions)) {
    if (isMapTransitionPalette(paletteType, canvasNode.data.config)) {
      apiNode.transitions.forEach((transition) => {
        if (!transition?.nextNode) return;
        const handleKey = transition.condition ?? transition.optionId ?? transition.id;
        if (handleKey != null && handleKey !== "") {
          addEdge(String(handleKey), transition.nextNode);
        }
      });
    } else {
      apiNode.transitions.forEach((transition) => {
        if (!transition?.nextNode) return;
        const handleKey = transition.condition ?? transition.optionId;
        if (handleKey != null && handleKey !== "") {
          addEdge(resolveTransitionHandleFromApi(String(handleKey), paletteType, canvasNode.data.config), transition.nextNode);
        } else {
          addEdge(LINEAR_HANDLE, transition.nextNode);
        }
      });
    }
  }

  return finish();
};

export const mapWorkflowDefinitionToFlow = (definition) => {
  const sourceNodes = definition?.nodes || [];
  const allEdges = [];

  const nodes = sourceNodes.map((apiNode, index) => {
    const { node, edges } = nodeToFlowFragments(apiNode);
    node.position = resolveCanvasPosition(apiNode, index);
    allEdges.push(...edges);
    return node;
  });

  const edges = allEdges.map((edge) => ({
    ...edge,
    sourceHandle: LEGACY_HANDLE_MAP[edge.sourceHandle] || edge.sourceHandle
  }));

  return {
    nodes,
    edges,
    start: resolveWorkflowStart(nodes, edges, definition?.start),
    sessionTimeoutSeconds: definition?.sessionTimeoutSeconds ?? DEFAULT_SESSION_TIMEOUT_SECONDS,
    globalTransitions: definition?.globalTransitions ?? {}
  };
};

const attachSaveToContext = (apiNode, saveToContext) =>
  saveToContext && Object.keys(saveToContext).length ? { ...apiNode, saveToContext } : apiNode;

const attachTimeoutRef = (apiNode, outgoing, workflowType) => {
  if (!supportsTimeoutRef(workflowType)) return apiNode;
  const onTimeout = outgoing[TIMEOUT_HANDLE];
  return onTimeout ? { ...apiNode, onTimeout } : apiNode;
};

const buildNodeIdentity = (node, data, paletteType, workflowType, normalizedConfig) => {
  const identity = stripEmpty({
    id: node.id,
    name: data.label || node.id,
    config: normalizedConfig && Object.keys(normalizedConfig).length ? normalizedConfig : undefined
  });

  if (isPaletteDrivenNode(paletteType)) {
    identity.paletteType = paletteType;
  } else {
    identity.type = workflowType;
  }

  return identity;
};

const serializeConfigKeyValues = (config) => {
  if (!config || typeof config !== "object") return config;
  const next = { ...config };
  Object.keys(next).forEach((key) => {
    const val = next[key];
    if (Array.isArray(val)) {
      const isKeyValueArray = val.every(
        (item) => item && typeof item === "object" && "key" in item && "value" in item
      );
      if (isKeyValueArray) {
        next[key] = val.reduce((acc, row) => {
          if (row.key && String(row.key).trim()) {
            acc[String(row.key).trim()] = row.value ?? "";
          }
          return acc;
        }, {});
      }
    } else if (val && typeof val === "object") {
      next[key] = serializeConfigKeyValues(val);
    }
  });
  return next;
};

const flowNodeToApiNode = (node, edges) => {
  const data = node.data || {};
  const paletteType =
    data.paletteType ||
    inferPaletteTypeFromLegacyNode({
      paletteType: data.paletteType,
      type: data.workflowType,
      config: data.config
    });
  const workflowType =
    data.workflowType ||
    inferWorkflowTypeFromNode({
      type: data.workflowType,
      paletteType,
      config: data.config
    });
  const outgoing = getOutgoingByHandle(node.id, edges);
  const rawNormalizedConfig = sanitizeNodeConfigForSave(
    normalizeNodeConfigForPalette(
      data.config,
      paletteType,
      data.label || node.id
    ),
    paletteType
  );
  const normalizedConfig = serializeConfigKeyValues(rawNormalizedConfig);

  const rawSaveToContext = data.saveToContext;
  const saveToContext = Array.isArray(rawSaveToContext)
    ? rawSaveToContext.reduce((acc, row) => {
        if (row.key && String(row.key).trim()) {
          acc[String(row.key).trim()] = row.value ?? "";
        }
        return acc;
      }, {})
    : rawSaveToContext;

  let apiNode;

  if (workflowType === "CONDITION") {
    apiNode = stripEmpty({
      id: node.id,
      name: data.label || node.id,
      paletteType: PALETTE.CONDITION,
      logic: data.logic || "AND",
      conditions: serializeConditionsForSave(data.conditions || []),
      onTrue: outgoing[CONDITION_TRUE_HANDLE],
      onFalse: outgoing[CONDITION_FALSE_HANDLE],
    });
  } else if (workflowType === "COLLECT_INPUT") {
    apiNode = attachSaveToContext(
      attachTimeoutRef(
        stripEmpty({
          ...buildNodeIdentity(node, data, paletteType, workflowType, normalizedConfig),
          onReceived: outgoing[COLLECT_RECEIVED_HANDLE]
        }),
        outgoing,
        workflowType
      ),
      saveToContext
    );
  } else if (workflowType === "INVOKE_WORKFLOW") {
    apiNode = attachSaveToContext(
      stripEmpty({
        ...buildNodeIdentity(node, data, paletteType, workflowType, normalizedConfig),
        onCompleted: outgoing[INVOKE_COMPLETED_HANDLE]
      }),
      saveToContext
    );
  } else if (workflowType === "ACTION") {
    const config = normalizedConfig || {};
    
    if (config.action === "SET_VARIABLE") {
      const flatVars = getFlatVariables(config.input);
      const computedSaveToContext = {};
      Object.keys(flatVars).forEach(key => {
        computedSaveToContext[key] = `$result.${key}`;
      });
      
      apiNode = stripEmpty({
        id: node.id,
        name: data.label || node.id,
        type: "ACTION",
        action: "SET_VARIABLE",
        input: flatVars,
        retryPolicy: config.retryPolicy,
        saveToContext: Object.keys(computedSaveToContext).length ? computedSaveToContext : undefined,
        onSuccess: outgoing[ACTION_SUCCESS_HANDLE]
      });
    } else if (config.action === "CLEAR_VARIABLE") {
      const clearKeys = getClearKeys(config.input);
      const computedSaveToContext = {};
      clearKeys.forEach(key => {
        computedSaveToContext[key] = `$result.${key}`;
      });
      
      apiNode = stripEmpty({
        id: node.id,
        name: data.label || node.id,
        type: "ACTION",
        action: "CLEAR_VARIABLE",
        input: {
          keys: clearKeys
        },
        retryPolicy: config.retryPolicy,
        saveToContext: Object.keys(computedSaveToContext).length ? computedSaveToContext : undefined,
        onSuccess: outgoing[ACTION_SUCCESS_HANDLE]
      });
    } else {
      apiNode = stripEmpty({
        id: node.id,
        name: data.label || node.id,
        type: "ACTION",
        action: config.action,
        input: config.input,
        retryPolicy: config.retryPolicy,
        ...(saveToContext && Object.keys(saveToContext).length ? { saveToContext } : {}),
        onSuccess: outgoing[ACTION_SUCCESS_HANDLE]
      });
      if (config.action === "HTTP_REQUEST") {
        apiNode.onFailure = outgoing[ACTION_FAILURE_HANDLE];
      }
    }
  } else if (workflowType === "HANDOFF" || workflowType === "TRANSFER_WORKFLOW") {
    apiNode = attachSaveToContext(
      buildNodeIdentity(node, data, paletteType, workflowType, normalizedConfig),
      saveToContext
    );
  } else if (workflowType === "END") {
    apiNode = { id: node.id, name: data.label || node.id, type: "END" };
  } else if (usesOnSuccessRef(workflowType)) {
    apiNode = attachSaveToContext(
      stripEmpty({
        ...buildNodeIdentity(node, data, paletteType, workflowType, normalizedConfig),
        onSuccess: outgoing[ACTION_SUCCESS_HANDLE]
      }),
      saveToContext
    );
  } else if (usesTransitions(workflowType) || isPaletteDrivenNode(paletteType)) {
    const transitions = serializeTransitionMap(paletteType, outgoing, normalizedConfig);
    const base = buildNodeIdentity(node, data, paletteType, workflowType, normalizedConfig);

    if (!transitions || !Object.keys(transitions).length) {
      apiNode = attachSaveToContext(attachTimeoutRef(base, outgoing, workflowType), saveToContext);
    } else {
      apiNode = attachSaveToContext(
        attachTimeoutRef({ ...base, transitions }, outgoing, workflowType),
        saveToContext
      );
    }
  } else {
    apiNode = attachSaveToContext(
      buildNodeIdentity(node, data, paletteType, workflowType, normalizedConfig),
      saveToContext
    );
  }

  return attachCanvasPosition(apiNode, node);
};

export const mapFlowToWorkflowDefinition = (nodes, edges, options = {}) => {
  const mappedNodes = nodes.map((node) => flowNodeToApiNode(node, edges));
  const start = resolveWorkflowStart(nodes, edges, options.start);

  const definition = {
    start,
    sessionTimeoutSeconds: options.sessionTimeoutSeconds ?? DEFAULT_SESSION_TIMEOUT_SECONDS,
    nodes: mappedNodes
  };

  if (options.globalTransitions && Object.keys(options.globalTransitions).length > 0) {
    definition.globalTransitions = options.globalTransitions;
  }

  return definition;
};

export const makeWorkflowId = (name = "") =>
  name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

export { serializeWorkflowForBackend } from "./workflowBackendSerializer";
